import React from 'react';
import I18n from 'i18n-js';

import { gql } from '@reverbdotcom/commons/src/gql';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { ReverbLogoIcon } from '@reverbdotcom/cadence/icons/react';
import { ADMINISTRATOR_PERMISSION_LEVEL } from '@reverbdotcom/commons/src/constants';


import LogIn from './login';
import ReverbHeaderDesktopMenu from './reverb_header_desktop_menu';
import ReverbHeaderMobileMenu from './reverb_header_mobile_menu';
import classNames from 'classnames';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';

interface IProps {
  loginComponent?: React.ReactNode; // used to pass in an oauth login component from sandbox layout.tsx
  logoOnly?: boolean;
}

export function SimpleReverbHeader({ loginComponent, logoOnly = false }: IProps) {
  const [activeDropdownKey, setActiveDropdownKey] = React.useState<string>();
  const user = useUser();
  const { loggedOut } = user;

  const { data } = useQuery(
    SIMPLE_REVERB_HEADER_QUERY,
    {
      skip: user.loggedOut,
      ssr: false,
      variables: {
        isShopAdmin: !user.secondaryUserModeIsActive || user.managedShopPermission == ADMINISTRATOR_PERMISSION_LEVEL,
      },
    },
  );

  const me = data?.me;

  return (
    <div className={classNames('simple-reverb-header', { 'simple-reverb-header--logo-only': logoOnly })}>
      <div className="simple-reverb-header__main">
        <div className="simple-reverb-header__inner">
          <nav aria-label={I18n.t('discovery.reverbHeader.navigationRole')}>
            <ul className="simple-reverb-header__nav__links">
              {!logoOnly && <ReverbHeaderMobileMenu me={me} />}
              <li className="simple-reverb-header__show-on-mobile">
                <CoreLink
                  to="/"
                  className="simple-reverb-header__logo"
                  aria-label={I18n.t('discovery.reverbHeader.reverbLogo')}
                >
                  <ReverbLogoIcon />
                </CoreLink>
              </li>
            </ul>
            <div className={classNames(
              'simple-reverb-header__nav',
              'simple-reverb-header__nav--main',
            )}>
            </div>
            <div className="simple-reverb-header__skip-link">
              <CoreLink
                to={{
                  anchor: 'main-content',
                }}
                className="simple-reverb-header__skip-link__link"
              >
                {I18n.t('discovery.reverbHeader.skipLink')}
              </CoreLink>
            </div>
            {!logoOnly && <nav
              aria-label={I18n.t('discovery.reverbHeader.myReverb')}
              role="navigation"
              className="simple-reverb-header__nav simple-reverb-header__nav--account"
            >
              <ul className="simple-reverb-header__nav__links">
                {loggedOut && !loginComponent && <LogIn />}
                {loggedOut && loginComponent}
                {!loggedOut && (
                  <li className="simple-reverb-header__nav__link__auto-width">
                    <ReverbHeaderDesktopMenu
                      activeDropdownKey={activeDropdownKey}
                      setActiveDropdownKey={setActiveDropdownKey}
                      me={me}
                      hideMenuLabel
                    />
                  </li>
                )}
              </ul>
            </nav>}
          </nav>
        </div>
      </div>
    </div>
  );
}

const SIMPLE_REVERB_HEADER_QUERY = gql(`
  query Core_SimpleReverbHeader($isShopAdmin: Boolean!) {
    me {
      _id
      uuid
      shop {
        _id
        slug
        paymentMethod
      }
      reverbCredits {
        display
        currency
      }
      walletBalance {
        display
        amountCents
        currency
      }
      payoutBalances @include(if: $isShopAdmin) {
        display
        currency
      }
    }
  }
`);

export default SimpleReverbHeader;
